import { useCallback, useEffect, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { Button } from '@/components/common/Button'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

const SCALE_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 2]

export interface PreviewDocument {
  id: string
  filename: string
  mime_type?: string | null
}

interface DocumentPreviewModalProps {
  isOpen: boolean
  onClose: () => void
  /** Direkt mitgegebene Dokumentliste; alternativ fetchDocuments. */
  documents?: PreviewDocument[] | null
  /** Lädt die Dokumentliste beim Öffnen (z.B. Dashboard-Karte). */
  fetchDocuments?: () => Promise<PreviewDocument[]>
  initialDocumentId?: string
  fetchFile: (documentId: string) => Promise<Blob>
  onDownload?: (documentId: string, filename: string) => void
}

type PreviewKind = 'pdf' | 'image' | 'other'

function detectKind(filename: string, mimeType?: string | null): PreviewKind {
  const mime = (mimeType ?? '').toLowerCase()
  if (mime.includes('pdf') || filename.toLowerCase().endsWith('.pdf')) return 'pdf'
  if (mime.startsWith('image/')) return 'image'
  if (/\.(jpe?g|png|gif|webp|bmp)$/i.test(filename)) return 'image'
  return 'other'
}

export function DocumentPreviewModal({
  isOpen,
  onClose,
  documents,
  fetchDocuments,
  initialDocumentId,
  fetchFile,
  onDownload,
}: DocumentPreviewModalProps) {
  const [docs, setDocs] = useState<PreviewDocument[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [listLoading, setListLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [blob, setBlob] = useState<Blob | null>(null)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [numPages, setNumPages] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [scale, setScale] = useState(1)

  const fetchFileRef = useRef(fetchFile)
  fetchFileRef.current = fetchFile
  const fetchDocumentsRef = useRef(fetchDocuments)
  fetchDocumentsRef.current = fetchDocuments
  const initialIdRef = useRef(initialDocumentId)
  initialIdRef.current = initialDocumentId

  const activeDoc = docs.find(d => d.id === activeId) ?? null
  const kind = activeDoc ? detectKind(activeDoc.filename, activeDoc.mime_type) : 'other'

  const loadBlob = useCallback((documentId: string) => {
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchFileRef.current(documentId)
      .then(file => {
        if (cancelled) return
        setBlob(file)
        setBlobUrl(URL.createObjectURL(file))
      })
      .catch(() => {
        if (!cancelled) setError('Dokument konnte nicht geladen werden.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  const loadList = useCallback(() => {
    const fetcher = fetchDocumentsRef.current
    if (!fetcher) return
    let cancelled = false
    setListLoading(true)
    setError(null)
    fetcher()
      .then(list => {
        if (cancelled) return
        setDocs(list)
        const wanted = list.find(d => d.id === initialIdRef.current)
        setActiveId(wanted ? wanted.id : (list[0]?.id ?? null))
      })
      .catch(() => {
        if (!cancelled) setError('Dokumente konnten nicht geladen werden.')
      })
      .finally(() => {
        if (!cancelled) setListLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!isOpen) return undefined
    setPageNumber(1)
    setNumPages(0)
    setBlob(null)
    if (documents && documents.length > 0) {
      setDocs(documents)
      const wanted = documents.find(d => d.id === initialIdRef.current)
      setActiveId(wanted ? wanted.id : (documents[0]?.id ?? null))
      return undefined
    }
    setDocs([])
    setActiveId(null)
    if (fetchDocumentsRef.current) {
      return loadList()
    }
    return undefined
  }, [isOpen, documents, loadList])

  // Blob laden, sobald ein Dokument aktiv ist (Wechsel = neues Laden)
  useEffect(() => {
    if (!isOpen || !activeId) return
    setPageNumber(1)
    setNumPages(0)
    const cancel = loadBlob(activeId)
    return cancel
  }, [isOpen, activeId, loadBlob])

  // Alte Object-URL freigeben, sobald eine neue gesetzt wird bzw. beim Unmount
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
  }, [blobUrl])

  useEffect(() => {
    if (!isOpen) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  const handleZoomIn = useCallback(() => {
    const next = SCALE_OPTIONS[SCALE_OPTIONS.indexOf(scale) + 1]
    if (next !== undefined) setScale(next)
  }, [scale])

  const handleZoomOut = useCallback(() => {
    const prev = SCALE_OPTIONS[SCALE_OPTIONS.indexOf(scale) - 1]
    if (prev !== undefined) setScale(prev)
  }, [scale])

  const handleDownload = useCallback(() => {
    if (!activeDoc) return
    if (onDownload) {
      onDownload(activeDoc.id, activeDoc.filename)
      return
    }
    if (!blob) return
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = activeDoc.filename
    a.click()
    URL.revokeObjectURL(url)
  }, [activeDoc, blob, onDownload])

  const handleRetry = useCallback(() => {
    if (fetchDocumentsRef.current && docs.length === 0) {
      loadList()
    } else if (activeId) {
      loadBlob(activeId)
    }
  }, [activeId, docs.length, loadBlob, loadList])

  const handleSelectDocument = useCallback((documentId: string) => {
    setActiveId(documentId)
  }, [])

  if (!isOpen) return null

  const canPaginate = kind === 'pdf' && numPages > 1
  const busy = listLoading || loading

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900 truncate" title={activeDoc?.filename}>
            {activeDoc?.filename ?? 'Dokumente'}
          </h2>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button variant="secondary" size="sm" onClick={handleDownload} disabled={!activeDoc || (!blob && !onDownload)}>
              <Download className="h-4 w-4 mr-1.5" />
              Download
            </Button>
            <button
              onClick={onClose}
              aria-label="Schliessen"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {docs.length > 1 && (
          <div className="flex items-center gap-2 px-4 py-2 border-b bg-gray-50">
            <span className="text-xs text-gray-500 shrink-0">Dokument:</span>
            <select
              value={activeId ?? ''}
              onChange={e => handleSelectDocument(e.target.value)}
              aria-label="Dokument auswählen"
              className="flex-1 min-w-0 text-sm border border-gray-300 rounded-md px-2 py-1 bg-white"
            >
              {docs.map((d, i) => (
                <option key={d.id} value={d.id}>
                  {i + 1}. {d.filename}
                </option>
              ))}
            </select>
          </div>
        )}

        {kind === 'pdf' && (
          <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50">
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleZoomOut}
                disabled={scale === SCALE_OPTIONS[0]}
                title="Verkleinern"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleZoomIn}
                disabled={scale === SCALE_OPTIONS[SCALE_OPTIONS.length - 1]}
                title="Vergrössern"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <span className="text-xs text-gray-600 w-12 text-center">
                {Math.round(scale * 100)}%
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                disabled={!canPaginate || pageNumber <= 1}
                title="Vorherige Seite"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-gray-600 min-w-[90px] text-center">
                {numPages > 0 ? `Seite ${pageNumber} von ${numPages}` : 'Laden…'}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPageNumber(p => Math.min(numPages, p + 1))}
                disabled={!canPaginate || pageNumber >= numPages}
                title="Nächste Seite"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-auto bg-gray-100 p-4">
          {busy && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-500">Dokument wird geladen…</span>
            </div>
          )}

          {!busy && error && (
            <div className="text-center text-red-600 py-16">
              <p>{error}</p>
              <div className="flex gap-2 mt-3 justify-center">
                <Button variant="secondary" size="sm" onClick={handleRetry}>
                  Erneut versuchen
                </Button>
                <Button variant="secondary" size="sm" onClick={onClose}>
                  Schliessen
                </Button>
              </div>
            </div>
          )}

          {!busy && !error && kind === 'pdf' && blobUrl && (
            <div className="flex justify-center">
              <Document
                file={blobUrl}
                onLoadSuccess={({ numPages: total }) => setNumPages(total)}
                onLoadError={() => setError('Das PDF konnte nicht angezeigt werden.')}
                loading={
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                    <span className="ml-2 text-gray-500">PDF wird gerendert…</span>
                  </div>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="shadow-lg"
                />
              </Document>
            </div>
          )}

          {!busy && !error && kind === 'image' && blobUrl && (
            <div className="flex justify-center">
              <img
                src={blobUrl}
                alt={activeDoc?.filename ?? ''}
                className="max-w-full max-h-[70vh] object-contain shadow-lg rounded"
              />
            </div>
          )}

          {!busy && !error && kind === 'other' && blobUrl && (
            <div className="text-center text-gray-600 py-16">
              <p className="mb-1 font-medium">Für diesen Dateityp ist keine Vorschau verfügbar.</p>
              <p className="text-sm text-gray-500 mb-4">{activeDoc?.filename}</p>
              <Button variant="primary" size="sm" onClick={handleDownload}>
                <Download className="h-4 w-4 mr-1.5" />
                Datei herunterladen
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
